/**
 * AuthContext.js — Global Authentication State Manager
 *
 * Provides login/logout state and user info to every component in the app.
 * Uses React Context so any component can access auth state without prop drilling.
 *
 * Session persistence:
 *   Uses sessionStorage (cleared when browser tab closes).
 *   TODO (Phase 2): Switch to httpOnly cookies for production security.
 *
 * JWT Refresh token rotation (L1-2):
 *   After login the access token AND refresh token are stored in sessionStorage.
 *   The api.js module store is kept in sync so fetchWithAuth() can silently
 *   refresh the access token and retry a failed request without any React re-render.
 *   The callbacks registered here update React state after a silent refresh.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { authenticate, logout as apiLogout, initApiAuth, clearApiAuth } from '../utils/api';
import { applyBrandTheme } from '../utils/brandTheme';
import { useIdleTimer } from '../hooks/useIdleTimer';
import SessionTimeoutModal from '../components/SessionTimeoutModal';
import logger from '../utils/logger';

// How long until the session timeout warning fires (default 15 min idle → 2 min warning)
const IDLE_TIMEOUT_MS    = 15 * 60 * 1000; // 15 minutes of inactivity
const WARNING_TIMEOUT_MS =  2 * 60 * 1000; // show warning 2 min before auto-logout

// ─── Context Creation ─────────────────────────────────────────────────────────

const AuthContext = createContext(null);

// ─── Auth Provider Component ──────────────────────────────────────────────────

/**
 * Wrap your entire app with <AuthProvider> to make auth state globally available.
 */
export function AuthProvider({ children }) {
  const [user,         setUser]         = useState(null);
  const [token,        setToken]        = useState(null);
  const [refreshToken, setRefreshToken] = useState(null); // Long-lived refresh token
  const [brand,        setBrand]        = useState(null);  // White-labelling: { brand_name, brand_color }
  const [loading,      setLoading]      = useState(false); // Login request in progress
  const [error,        setError]        = useState(null);  // Last login error message
  const [initializing, setInitializing] = useState(true);  // True until session restore completes

  // ── Session timeout state ───────────────────────────────────────────────────
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  const [warningSecondsLeft, setWarningSecondsLeft] = useState(0);
  const warningTickRef = useRef(null); // interval that decrements the countdown

  // ── Logout (defined early — referenced by idle handler + api store) ─────────
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const logout = useCallback(async () => {
    logger.info('Auth: Logging out', { userId: user?.id });
    clearApiAuth(); // Remove tokens from api module store immediately

    try {
      // Pass both tokens so the backend blacklists both
      const storedToken   = sessionStorage.getItem('cai_token');
      const storedRefresh = sessionStorage.getItem('cai_refresh_token');
      if (storedToken) {
        await apiLogout(storedToken, storedRefresh);
      }
    } catch (err) {
      logger.warn('Auth: Logout API call failed', err);
    } finally {
      // Clear storage and React state regardless of API result
      sessionStorage.removeItem('cai_token');
      sessionStorage.removeItem('cai_refresh_token');
      sessionStorage.removeItem('cai_user');
      sessionStorage.removeItem('cai_brand');
      setToken(null);
      setRefreshToken(null);
      setUser(null);
      setBrand(null);
      setError(null);
      applyBrandTheme(null);   // reset to default theme on logout
      logger.info('Auth: Session cleared');
    }
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Helper: register tokens with the api module store ──────────────────────
  // Called after login AND after session restore so fetchWithAuth can
  // silently refresh without React state involvement.
  const registerApiTokens = useCallback((accessToken, refToken) => {
    initApiAuth(
      accessToken,
      refToken,
      // onTokenRefresh: fetchWithAuth calls this after a successful silent refresh
      (newAccessToken) => {
        logger.info('Auth: Access token silently refreshed');
        sessionStorage.setItem('cai_token', newAccessToken);
        setToken(newAccessToken);
      },
      // onSessionEnd: fetchWithAuth calls this when refresh fails (token expired/revoked)
      () => {
        logger.warn('Auth: Refresh token expired or revoked — logging out');
        logout();
      },
    );
  }, [logout]);

  // ── Restore session on page refresh ────────────────────────────────────────
  // initializing stays true until this effect runs — routes render nothing during
  // this time to avoid the race condition where /change-password redirects to /login
  // before the session (including must_change_password flag) is restored.
  useEffect(() => {
    try {
      const savedToken   = sessionStorage.getItem('cai_token');
      const savedRefresh = sessionStorage.getItem('cai_refresh_token');
      const savedUser    = sessionStorage.getItem('cai_user');
      const savedBrand   = sessionStorage.getItem('cai_brand');

      if (savedToken && savedUser) {
        const parsedUser = JSON.parse(savedUser);
        setToken(savedToken);
        setRefreshToken(savedRefresh || null);
        setUser(parsedUser);
        const parsedBrand = savedBrand ? JSON.parse(savedBrand) : null;
        if (parsedBrand) setBrand(parsedBrand);
        applyBrandTheme(parsedBrand);   // re-apply theme on page refresh

        // Re-register with api module store so fetchWithAuth can still refresh
        registerApiTokens(savedToken, savedRefresh || null);
        logger.info('Auth: Session restored from storage');
      }
    } catch (err) {
      logger.warn('Auth: Could not restore session', err);
      sessionStorage.clear();
    } finally {
      setInitializing(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Login ───────────────────────────────────────────────────────────────────
  const login = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    logger.info('Auth: Initiating login', { email });

    try {
      const { token: newToken, refreshToken: newRefresh, user: newUser, brand: newBrand }
        = await authenticate(email, password);

      // Persist to sessionStorage
      sessionStorage.setItem('cai_token', newToken);
      sessionStorage.setItem('cai_user',  JSON.stringify(newUser));
      if (newRefresh) sessionStorage.setItem('cai_refresh_token', newRefresh);
      if (newBrand)   sessionStorage.setItem('cai_brand', JSON.stringify(newBrand));

      setToken(newToken);
      setRefreshToken(newRefresh || null);
      setUser(newUser);
      setBrand(newBrand || null);
      applyBrandTheme(newBrand || null);  // apply tenant theme immediately after login

      // Register with api module store
      // Note: forced-reset users have newRefresh=null (5-min access token only)
      registerApiTokens(newToken, newRefresh || null);

      logger.info('Auth: Login successful', { userId: newUser.id, forcedReset: newUser.must_change_password });
      return true;

    } catch (err) {
      const message = err.message || 'Login failed. Please try again.';
      logger.error('Auth: Login failed', err);
      setError(message);
      return false;

    } finally {
      setLoading(false);
    }
  }, [registerApiTokens]);

  // ── Session timeout helpers ─────────────────────────────────────────────────

  const stopWarningTick = useCallback(() => {
    clearInterval(warningTickRef.current);
    warningTickRef.current = null;
  }, []);

  const handleIdleWarning = useCallback(() => {
    if (!user) return;
    logger.info('Auth: Idle warning shown');
    const totalSecs = Math.round(WARNING_TIMEOUT_MS / 1000);
    setWarningSecondsLeft(totalSecs);
    setShowTimeoutWarning(true);

    warningTickRef.current = setInterval(() => {
      setWarningSecondsLeft((prev) => {
        if (prev <= 1) { clearInterval(warningTickRef.current); return 0; }
        return prev - 1;
      });
    }, 1000);
  }, [user]);

  const handleIdle = useCallback(() => {
    if (!user) return;
    stopWarningTick();
    setShowTimeoutWarning(false);
    logger.info('Auth: Session expired due to inactivity — auto-logging out');
    logout();
  }, [user, stopWarningTick, logout]);

  const handleStayActive = useCallback(() => {
    stopWarningTick();
    setShowTimeoutWarning(false);
    logger.info('Auth: User chose to stay signed in');
  }, [stopWarningTick]);

  useIdleTimer({
    idleTimeout:    IDLE_TIMEOUT_MS,
    warningTimeout: WARNING_TIMEOUT_MS,
    onWarning:      handleIdleWarning,
    onIdle:         handleIdle,
    onActive:       () => {
      if (showTimeoutWarning) { stopWarningTick(); setShowTimeoutWarning(false); }
    },
    enabled: !!user,
  });

  // ── Clear error ─────────────────────────────────────────────────────────────
  const clearError = useCallback(() => setError(null), []);

  // ── Patch user state (e.g., clear must_change_password after forced reset) ──
  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, ...patch };
      sessionStorage.setItem('cai_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  // ── Upgrade a forced-reset session to a full session after password change ──
  // Called by ForceChangePasswordPage when the backend returns new tokens.
  const upgradeSession = useCallback((newAccessToken, newRefreshToken) => {
    sessionStorage.setItem('cai_token', newAccessToken);
    sessionStorage.setItem('cai_refresh_token', newRefreshToken);
    setToken(newAccessToken);
    setRefreshToken(newRefreshToken);
    registerApiTokens(newAccessToken, newRefreshToken);
  }, [registerApiTokens]);

  // ── Context value ───────────────────────────────────────────────────────────
  const value = {
    user,            // Current user object (null if not logged in)
    token,           // JWT access token (null if not logged in)
    refreshToken,    // JWT refresh token (null for forced-reset sessions / if logged out)
    brand,           // Tenant branding: { brand_name, brand_color } or null
    isAuthenticated: !!user,
    initializing,    // True until sessionStorage restore is done — routes should wait
    loading,         // True while login request is pending
    error,           // Error message from last login attempt
    login,           // Function: login(email, password) → Promise<boolean>
    logout,          // Function: logout() → Promise<void>
    clearError,      // Function: clears the error state
    updateUser,      // Function: updateUser(patch) → merges patch into stored user
    upgradeSession,  // Function: upgradeSession(token, refreshToken) → replaces short forced-reset token
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      {/* Session timeout warning modal — rendered at root so it always appears on top */}
      <SessionTimeoutModal
        visible={showTimeoutWarning}
        secondsLeft={warningSecondsLeft}
        onStay={handleStayActive}
        onLogout={logout}
      />
    </AuthContext.Provider>
  );
}

// ─── Custom Hook ─────────────────────────────────────────────────────────────

/**
 * useAuth() — Access auth state in any component.
 *
 * Usage:
 *   const { user, login, logout, isAuthenticated } = useAuth();
 */
export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used inside <AuthProvider>. Check your App.js.');
  }
  return ctx;
}

export default AuthContext;
